import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';
import { HardHat, MapPin, ChevronRight, Wallet } from 'lucide-react';
import { formatUsd, invoiceTotals } from '@/lib/jobsData';
import { getPhase, getPhaseProgressPct, PHASE_MAX_INDEX } from '@/lib/jobPhases';
import { statusLabel } from '@/lib/statusLabels';
import { translatePhase } from '@/lib/i18nLabels';

const statusBadgeStyles = {
  active:
    'bg-[hsl(var(--success))]/15 text-[hsl(var(--success))] border-[hsl(var(--success))]/30',
  closed:
    'bg-[hsl(var(--muted))] text-muted-foreground border-border',
  pending:
    'bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))] border-[hsl(var(--warning))]/30',
};

export const JobCard = ({ job, onOpen }) => {
  const { t } = useTranslation();
  const phase = Number(job?.phase) || 0;
  const phaseInfo = getPhase(phase);
  const phasePct = getPhaseProgressPct(phase);
  const totals = invoiceTotals(job?.invoices || []);
  const collectedPct =
    Number(job?.contract) > 0
      ? Math.min(100, Math.round((totals.paid / (Number(job.contract) || 1)) * 100))
      : 0;
  const badgeClass = statusBadgeStyles[job?.status] || statusBadgeStyles.active;

  return (
    <button
      type="button"
      onClick={() => onOpen?.(job)}
      className="w-full text-left group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]/40 rounded-lg"
      data-testid={`job-card-${job?.id}`}
    >
      <Card className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))] hover:border-[hsl(var(--primary))]/30 transition-colors">
        <CardContent className="p-4 sm:p-5">
          <div className="flex items-start gap-3">
            <div className="size-9 rounded-md border border-border bg-[hsl(var(--muted))] flex items-center justify-center text-[hsl(var(--primary))] shrink-0">
              <HardHat size={18} strokeWidth={1.75} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="text-sm sm:text-base font-semibold text-foreground truncate">
                  {job?.name || t('jobs.card.untitled')}
                </h3>
                <Badge
                  variant="outline"
                  className={cn('uppercase text-[10px] tracking-[0.16em]', badgeClass)}
                >
                  {statusLabel(t, job?.status || 'active')}
                </Badge>
              </div>
              <div className="mt-0.5 text-xs text-muted-foreground truncate">
                {job?.client || '—'}
              </div>
              {job?.address ? (
                <div className="mt-1 flex items-center gap-1.5 text-[11px] text-muted-foreground/80 truncate">
                  <MapPin size={12} strokeWidth={2} />
                  <span className="truncate">{job.address}</span>
                </div>
              ) : null}
            </div>
            <ChevronRight
              size={16}
              className="shrink-0 text-muted-foreground group-hover:text-[hsl(var(--primary))] transition-colors"
            />
          </div>

          <div className="mt-4 grid grid-cols-3 gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                {t('jobs.card.contract')}
              </div>
              <div className="mt-0.5 text-sm font-semibold text-foreground inline-flex items-center gap-1.5">
                <Wallet size={12} className="text-muted-foreground" />
                {formatUsd(job?.contract)}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                {t('jobs.card.phase')}
              </div>
              <div className="mt-0.5 text-sm font-semibold text-foreground truncate">
                {translatePhase(t, phaseInfo.key)}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                {t('jobs.card.collected')}
              </div>
              <div className="mt-0.5 text-sm font-semibold text-[hsl(var(--success))]">
                {collectedPct}%
              </div>
            </div>
          </div>

          <div className="mt-4">
            <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
              <span>{t('jobs.card.phase_progress')}</span>
              <span className="text-foreground/80">
                {phase} / {PHASE_MAX_INDEX}
              </span>
            </div>
            <Progress
              value={phasePct}
              className="h-1.5 mt-2 bg-[hsl(var(--muted))] [&>div]:bg-[hsl(var(--primary))]"
            />
          </div>
        </CardContent>
      </Card>
    </button>
  );
};

export default JobCard;
